import {
  addDoc,
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  type DocumentReference,
  type DocumentSnapshot,
  type FirestoreDataConverter,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  type QueryDocumentSnapshot,
  type QuerySnapshot,
  setDoc,
  type SnapshotOptions,
  type Unsubscribe,
  where,
  type WithFieldValue,
} from 'firebase/firestore';
import { getFirestoreInstance } from './firestoreClient';

export const firestoreVenueConverter: FirestoreDataConverter<Pagcomanda.Venue> = {
  toFirestore(venue: WithFieldValue<Pagcomanda.Venue>) {
    return venue;
  },
  fromFirestore(
    snapshot: QueryDocumentSnapshot,
    options?: SnapshotOptions,
  ): Pagcomanda.Venue {
    const data = snapshot.data(options);

    if (!data) {
      throw new Error('Venue document data is empty');
    }

    return data as Pagcomanda.Venue;
  },
};

export const firestoreVenueUserConverter: FirestoreDataConverter<Pagcomanda.VenueUser> = {
  toFirestore(venueUser: WithFieldValue<Pagcomanda.VenueUser>) {
    return venueUser;
  },
  fromFirestore(
    snapshot: QueryDocumentSnapshot,
    options?: SnapshotOptions,
  ): Pagcomanda.VenueUser {
    const data = snapshot.data(options);

    if (!data) {
      throw new Error('Venue user document data is empty');
    }

    return data as Pagcomanda.VenueUser;
  },
};

export const firestorePaymentMethodsConverter: FirestoreDataConverter<Pagcomanda.PaymentMethod> = {
  toFirestore(paymentMethod: WithFieldValue<Pagcomanda.PaymentMethod>) {
    return paymentMethod;
  },
  fromFirestore(
    snapshot: QueryDocumentSnapshot,
    options?: SnapshotOptions,
  ): Pagcomanda.PaymentMethod {
    const data = snapshot.data(options);

    if (!data) {
      throw new Error('Payment method document data is empty');
    }

    return data as Pagcomanda.PaymentMethod;
  },
};

export function getVenueDocRef(venueId: string): DocumentReference<Pagcomanda.Venue> {
  return doc(
    getFirestoreInstance(),
    'venues',
    venueId,
  ).withConverter(firestoreVenueConverter);
}

export async function getVenue(venueId: string): Promise<DocumentSnapshot<Pagcomanda.Venue>> {
  return getDoc(getVenueDocRef(venueId));
}

function getVenuePaymentMethodsCollection(
  venueRefOrId: DocumentReference<Pagcomanda.Venue> | string,
) {
  const venueRef = typeof venueRefOrId === 'string'
    ? getVenueDocRef(venueRefOrId)
    : venueRefOrId;

  return collection(venueRef, 'paymentMethods').withConverter(
    firestorePaymentMethodsConverter,
  );
}

export function getVenuePaymentMethods(
  venueRefOrId: DocumentReference<Pagcomanda.Venue> | string,
): Promise<QuerySnapshot<Pagcomanda.PaymentMethod>> {
  return getDocs(getVenuePaymentMethodsCollection(venueRefOrId));
}

export function observeVenuePaymentMethods(
  venueRefOrId: DocumentReference<Pagcomanda.Venue> | string,
  onNext: (snapshot: QuerySnapshot<Pagcomanda.PaymentMethod>) => void,
): Unsubscribe {
  const q = query(
    getVenuePaymentMethodsCollection(venueRefOrId),
    orderBy('description'),
  );

  return onSnapshot(q, onNext);
}

export async function addVenuePaymentMethod(
  venueRefOrId: DocumentReference<Pagcomanda.Venue> | string,
  paymentMethod: WithFieldValue<Pagcomanda.PaymentMethod>,
): Promise<string> {
  const docRef = await addDoc(
    getVenuePaymentMethodsCollection(venueRefOrId),
    paymentMethod,
  );

  return docRef.id;
}

export function saveVenuePaymentMethod(
  venueRefOrId: DocumentReference<Pagcomanda.Venue> | string,
  paymentMethodId: string,
  paymentMethod: WithFieldValue<Pagcomanda.PaymentMethod>,
): Promise<void> {
  const ref = doc(
    getVenuePaymentMethodsCollection(venueRefOrId),
    paymentMethodId,
  );

  return setDoc(ref, paymentMethod, { merge: true });
}

export function deleteVenuePaymentMethod(
  venueRefOrId: DocumentReference<Pagcomanda.Venue> | string,
  paymentMethodId: string,
): Promise<void> {
  const ref = doc(
    getVenuePaymentMethodsCollection(venueRefOrId),
    paymentMethodId,
  );

  return deleteDoc(ref);
}

export function observeVenueUsersForUser(
  userId: string,
  onNext: (snapshot: QuerySnapshot<Pagcomanda.VenueUser>) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  const q = query(
    collectionGroup(getFirestoreInstance(), 'venueUsers'),
    where('userId', '==', userId),
  ).withConverter(firestoreVenueUserConverter);

  return onError ? onSnapshot(q, onNext, onError) : onSnapshot(q, onNext);
}
